import { itertools, map, sort } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import { client as apiv2Client } from '@/client-v2';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
/** @returns The initial state. */
function makeInitialState() {
    return {
        sessions: {},
    };
}
const moduleBuilder = storeBuilder.module('editor-sessions', makeInitialState());
/**
 * Get the state for the given assignment and user.
 *
 * @param state The full state of the store.
 * @param assignmentId The assignment id to get the state for.
 * @param userId The user id to get the state for.
 * @returns The found data or undefined if not set.
 */
function getState(state, assignmentId, userId) {
    const byAssig = state.sessions[assignmentId];
    return byAssig?.[userId];
}
/** The store for editor sessions. */
export var EditorSessionsStore;
(function (EditorSessionsStore) {
    const allSessions = moduleBuilder.read(state => new map.ReadonlyMaybeMap(Object.values(state.sessions).flatMap(byUser => Object.values(byUser).flatMap(({ data: sessions }) => (sessions ?? []).map((session) => [session.editorSessionId, session])))), 'allSessions');
    /** Get the session by session id. */
    EditorSessionsStore.getSession = moduleBuilder.read(() => {
        const sessions = allSessions();
        return (sessionId) => sessions.get(sessionId);
    }, 'getSession');
    /** Get loading state for a specific assignment and user. */
    EditorSessionsStore.getSessions = moduleBuilder.read(state => 
    /**
     * Gets the loading state from a given state.
     *
     * @param assignmentId Assignment ID.
     * @param userId User ID.
     * @returns The loading state.
     */
    (assignmentId, userId) => utils.getPaginatedState(getState(state, assignmentId, userId)), 'getSessions');
    /** Set sessions for a specific assignment and user. */
    const commitSessions = moduleBuilder.commit((state, { assignmentId, userId, sessions, }) => {
        const byAssignment = utils.vueSetDefault(state.sessions, assignmentId, {});
        const newData = Object.freeze(Array.from(itertools.uniqueSorted(sort.sortBy(sessions.data ?? [], session => [session.createdAt, session.editorSessionId], {
            reverse: true,
        }), (a, b) => a.editorSessionId === b.editorSessionId)));
        const forUser = Object.freeze({
            ...sessions,
            data: newData,
        });
        utils.vueSet(byAssignment, userId, forUser);
    }, 'commitSessions');
    const addSession = moduleBuilder.commit((state, { editorSession, }) => {
        const assignmentId = parseInt(editorSession.assignmentId, 10);
        const userId = parseInt(editorSession.owner, 10);
        const existing = getState(state, assignmentId, userId);
        commitSessions({
            assignmentId,
            userId,
            sessions: {
                pagination: Object.freeze({ tag: 'not-started' }),
                ...existing,
                data: (existing?.data ?? []).concat([editorSession]),
            },
        });
    }, 'addSession');
    /** Clear all reset state. */
    EditorSessionsStore.commitClearState = moduleBuilder.commit(state => {
        Object.assign(state, makeInitialState());
    }, 'commitClearState');
    /** Load the a session page. */
    EditorSessionsStore.loadSessionsPage = moduleBuilder.dispatch(async ({ state }, { assignmentId, userId, toLoad, }) => {
        await utils.loadPaginatedWithForce({
            getData: () => getState(state, assignmentId, userId),
            toLoad,
            load: async (nextToken) => {
                const { ListEditorSessionsCommand } = await import('@codegrade/apiv2-client');
                const response = await apiv2Client.send(new ListEditorSessionsCommand({
                    assignmentId: assignmentId.toString(),
                    userId: userId.toString(),
                    nextToken,
                }));
                return {
                    data: response.editorSessions,
                    nextToken: response.nextToken,
                };
            },
            commitData: sessions => commitSessions({ assignmentId, userId, sessions }),
        });
    }, 'loadSessionsPage');
    /** Load sessions for a specific assignment and user. */
    EditorSessionsStore.loadTillCurrentSessions = moduleBuilder.dispatch(async (ctx, { assignmentId, userId, currentSessionId, }) => {
        /** @returns If we are finished loading. */
        function isFinished() {
            const state = getState(ctx.state, assignmentId, userId);
            if (state == null || state.pagination.tag === 'not-started') {
                return false;
            }
            else if (state.pagination.tag === 'finished') {
                return true;
            }
            return (state.data ?? []).some(d => d.editorSessionId === currentSessionId);
        }
        while (!isFinished()) {
            // eslint-disable-next-line no-await-in-loop
            await EditorSessionsStore.loadSessionsPage({ assignmentId, toLoad: 'next-page', userId });
        }
    }, 'loadTillCurrentSessions');
    /** Load a single session by id. */
    EditorSessionsStore.loadSingleSession = moduleBuilder.dispatch(async (_, { editorSessionId, force = false, }) => {
        if (!force && allSessions().get(editorSessionId).isJust()) {
            return;
        }
        const { GetEditorSessionCommand } = await import('@codegrade/apiv2-client');
        const { editorSession } = await apiv2Client.send(new GetEditorSessionCommand({ editorSessionId }));
        addSession({ editorSession });
    }, 'loadSingleSession');
    /** Create a new editor session and add it to the store. */
    EditorSessionsStore.createEditorSession = moduleBuilder.dispatch(async (_, data) => {
        const { CreateEditorSessionCommand } = await import('@codegrade/apiv2-client');
        const resp = await apiv2Client.send(new CreateEditorSessionCommand(data));
        addSession({ editorSession: resp.editorSession });
        return resp;
    }, 'createEditorSession');
    /** Reset the store when the user changes. */
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        EditorSessionsStore.commitClearState();
    });
})(EditorSessionsStore || (EditorSessionsStore = {}));
